// g2-plan-readiness.ts
//
// H4 A1 (design bản 2, 2026-09-29): G2 approves a PLAN, so it is only
// decided once a plan exists. Measured on 3.33.0-beta.5: G2 went `pending`
// (or `waived` under G0 delegation) at tick 0, with only the two bootstrap
// tickets on disk; an approval given then stayed valid while the plan grew by
// 16 tickets, and a `waived` G2 let a pre-existing feature ticket run three
// times while the plan ticket itself was blocked.
//
// "Plan ready" = every effective bootstrap ticket (survey/plan, not
// cancelled/superseded) is `done`. Recomputed every tick from the tickets on
// disk + journal statuses — no state that could go stale.
import { BOOTSTRAP_TICKET_KINDS } from "./feature-dispatch-gate-guard.js";
import type { Ticket } from "./schemas/ticket-schema.js";

/** Statuses that take a ticket out of the plan (shared with g2-plan-content-snapshot.ts). */
export const RETIRED_TICKET_STATUSES: ReadonlySet<string> = new Set(["cancelled", "superseded"]);

export interface G2PlanReadiness {
  ready: boolean;
  /** Set when not ready — names the bootstrap tickets still open, for the operator. */
  reason?: string;
  /**
   * Review M1: read-only decision tickets the controller wrote from a
   * `redirect --text` (D5). They may run while the plan is not ready — a
   * redirect is how an operator unsticks a blocked plan ticket.
   */
  exemptTicketIds?: readonly string[];
}

export function assessG2PlanReadiness(
  tickets: readonly Ticket[],
  ticketStatus: Record<string, string>,
  exemptTicketIds: readonly string[] = [],
): G2PlanReadiness {
  const open = tickets.filter((t) => {
    const status = ticketStatus[t.id] ?? "todo";
    return (
      BOOTSTRAP_TICKET_KINDS.includes(t.kind) &&
      !RETIRED_TICKET_STATUSES.has(status) &&
      status !== "done"
    );
  });
  if (open.length === 0) return { ready: true, exemptTicketIds };
  const detail = open
    .map((t) => `${t.id} (${t.kind}) = ${ticketStatus[t.id] ?? "todo"}`)
    .join(", ");
  return {
    ready: false,
    exemptTicketIds,
    reason:
      `plan chưa sẵn sàng: ${detail} — G2 chỉ được xét khi plan xong, feature chờ tới lúc đó ` +
      '(ticket plan bị blocked: chỉ đạo lại bằng `avatar autopilot redirect --text "<mô tả>"` — ticket ' +
      "decision sinh ra được chạy ngay — hoặc `avatar autopilot abort`).",
  };
}
